import { Alert as AntAlert, Button, Popconfirm, Switch, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BranchesOutlined, CopyOutlined, PlusOutlined } from '@ant-design/icons';
import { formatRelative } from '@/lib/time';
import type { AlertRuleView } from '@/api/alerts';
import { EmptyState } from '@/components/EmptyState';
import { TableSkeleton } from '@/components/TableSkeleton';

const { Text, Paragraph } = Typography;

export interface RulesPanelProps {
  rules: AlertRuleView[];
  loading: boolean;
  /** 403 on GET /v1/alerts/rules — the read key is narrower than alerts.read. */
  readDenied: boolean;
  onNewRule: () => void;
  /**
   * "Duplicate as replacement" (OPSFL-FIX-1) — opens the create modal PREFILLED from this rule.
   * The honest correction path around the deliberate no-edit/no-delete posture.
   */
  onDuplicate: (rule: AlertRuleView) => void;
  onToggle: (rule: AlertRuleView, enabled: boolean) => void;
  /** Rule ids with a toggle in flight (disables the switch, no double-fire). */
  togglingIds: ReadonlySet<string>;
}

function MatchSummary({ rule }: { rule: AlertRuleView }) {
  const parts: string[] = [];
  if (rule.matchType) parts.push(`type = ${rule.matchType}`);
  if (rule.matchSeverity) parts.push(`severity = ${rule.matchSeverity}`);
  if (rule.matchSource) parts.push(`source = ${rule.matchSource}`);
  for (const [k, v] of Object.entries(rule.matchDetail ?? {})) parts.push(`${k} = ${String(v)}`);
  if (parts.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        matches every alert
      </Text>
    );
  }
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {parts.map((p) => (
        <Tag key={p} className="ol-mono" style={{ fontSize: 10.5, marginInlineEnd: 0 }}>
          {p}
        </Tag>
      ))}
    </span>
  );
}

function ActionSummary({ rule }: { rule: AlertRuleView }) {
  switch (rule.action) {
    case 'START_FLOW':
      return (
        <span style={{ fontSize: 12 }}>
          Start flow <Text className="ol-mono" style={{ fontSize: 11 }}>{rule.flowId}</Text>
          {rule.flowVersion != null && <Text type="secondary"> v{rule.flowVersion}</Text>}
        </span>
      );
    case 'SIGNAL_RUN':
      return (
        <span style={{ fontSize: 12 }}>
          Signal node <Text className="ol-mono" style={{ fontSize: 11 }}>{rule.nodeId}</Text> with{' '}
          <Text className="ol-mono" style={{ fontSize: 11 }}>{rule.decision}</Text>
        </span>
      );
    case 'SUPPRESS':
      return <Tag style={{ marginInlineEnd: 0 }}>SUPPRESS</Tag>;
    default:
      return <span style={{ fontSize: 12 }}>{rule.action}</span>;
  }
}

/**
 * Routing rules (OPSFL-ALUI-1) — the flat-equality rule catalogue over GET /v1/alerts/rules,
 * with the two mutations the API actually has: CREATE and enable/disable (`alerts.rules.write`,
 * hub_admin). There is deliberately no edit or delete: a correction is a new rule + disable,
 * so past routings stay explainable. First enabled rule by priority wins.
 */
export function RulesPanel({
  rules,
  loading,
  readDenied,
  onNewRule,
  onDuplicate,
  onToggle,
  togglingIds,
}: RulesPanelProps) {
  if (readDenied) {
    return (
      <AntAlert
        type="info"
        showIcon
        message="Routing rules are not visible to your role"
        description="Viewing rules needs alerts.rules.read (hub admin, hub operator or auditor). The register above still shows you every routing OUTCOME on your alerts."
      />
    );
  }

  const columns: ColumnsType<AlertRuleView> = [
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      width: 84,
      align: 'right',
      sorter: (a, b) => a.priority - b.priority,
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Rule',
      dataIndex: 'name',
      key: 'name',
      render: (v: string, r) => (
        <div style={{ minWidth: 160 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5 }}>{v}</div>
          <Text className="ol-mono" style={{ fontSize: 10, color: 'var(--ol-text-tertiary)' }}>
            {r.ruleId}
          </Text>
        </div>
      ),
    },
    { title: 'Match', key: 'match', render: (_, r) => <MatchSummary rule={r} /> },
    { title: 'Action', key: 'action', width: 230, render: (_, r) => <ActionSummary rule={r} /> },
    {
      title: 'Updated',
      key: 'updated',
      width: 110,
      render: (_, r) => (
        <Text style={{ fontSize: 11.5 }} title={r.updatedAt ?? r.createdAt ?? undefined}>
          {formatRelative(r.updatedAt ?? r.createdAt)}
        </Text>
      ),
    },
    {
      title: '',
      key: 'duplicate',
      width: 44,
      render: (_, r) => (
        <Tooltip title="Duplicate as replacement — the create modal opens prefilled from this rule; after creating you can disable this one. Rules are never edited in place.">
          <Button
            size="small"
            type="text"
            icon={<CopyOutlined />}
            aria-label={`Duplicate rule ${r.name} as replacement`}
            onClick={() => onDuplicate(r)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Enabled',
      key: 'enabled',
      width: 90,
      render: (_, r) => (
        <Popconfirm
          title={r.enabled ? 'Disable this rule?' : 'Enable this rule?'}
          description={
            r.enabled
              ? 'New matching alerts will stop routing through it (existing routings are untouched).'
              : 'It starts matching new alerts immediately, in priority order.'
          }
          onConfirm={() => onToggle(r, !r.enabled)}
          okText={r.enabled ? 'Disable' : 'Enable'}
        >
          <Switch
            size="small"
            checked={r.enabled}
            loading={togglingIds.has(r.ruleId)}
            aria-label={`${r.enabled ? 'Disable' : 'Enable'} rule ${r.name}`}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <Paragraph type="secondary" style={{ margin: 0, fontSize: 12.5, flex: 1, maxWidth: 720 }}>
          Rules turn the alert stream into governed casework: flat field=value conditions, first
          enabled rule by priority wins, and the action either starts a <b>published</b> flow,
          signals a running one, or suppresses. Rules cannot be edited in place or deleted —
          use <b>Duplicate as replacement</b> on a rule to open the create modal prefilled, then
          disable the original: two explicit acts, both recorded, so &ldquo;why did this fire in
          March&rdquo; stays answerable. Writes need{' '}
          <Text className="ol-mono" style={{ fontSize: 11 }}>alerts.rules.write</Text> (hub admin).
        </Paragraph>
        <Button type="primary" icon={<PlusOutlined />} onClick={onNewRule}>
          New rule
        </Button>
      </div>

      <Table<AlertRuleView>
        rowKey="ruleId"
        size="small"
        loading={loading && rules.length > 0}
        columns={columns}
        dataSource={rules}
        pagination={false}
        locale={{
          emptyText: loading ? (
            <TableSkeleton label="Loading routing rules" rows={4} />
          ) : (
            <EmptyState
              compact
              icon={<BranchesOutlined />}
              title="No routing rules yet"
              description="Every alert lands OPEN on the register until a rule routes it. A rule matches flat field=value conditions and, on the first match by priority, starts or signals a published flow — that is how an alert becomes governed casework."
            />
          ),
        }}
      />
    </div>
  );
}
